import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ClipboardCheck, Clock, ShieldCheck, User } from "lucide-react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";

const ONB = { pending: "Pendiente de empezar", in_progress: "Formulario en curso", completed: "Formulario enviado", draft_generated: "En preparación", pending_approval: "En preparación", active: "Completada" };
const SVC_COLOR = { active: "#4ade80", expiring: "#facc15", expired: "#f87171", unknown: "#a3a3a3" };
const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) : "—");

const Box = ({ icon: Icon, title, children, testid }) => (
  <div className="qv-card-app" style={{ padding: 20 }} data-testid={testid}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--qv-red)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>
      <Icon size={14} /> {title}
    </div>
    {children}
  </div>
);

export default function MyArea() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/me/area").then((r) => setD(r.data)).catch((e) => setErr(formatErr(e))); }, []);
  const s = d?.service;
  return (
    <AppShell>
      <div className="qv-page-head" data-testid="my-area-page"><div className="label">Tu cuenta</div><h1>Mi área</h1></div>
      {err && <p style={{ color: "#f87171" }}>{err}</p>}
      {!d && !err && <p style={{ color: "#a3a3a3" }}>Cargando…</p>}
      {d && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          <Box icon={User} title="Perfil" testid="my-area-profile">
            <div style={{ fontSize: 18, fontWeight: 700 }}>{d.profile.name}</div>
            <div style={{ fontSize: 13, color: "#a3a3a3", marginTop: 4, lineHeight: 1.7 }}>
              {d.profile.email}{d.profile.phone && <><br />{d.profile.phone}</>}
              {(d.profile.age || d.profile.position || d.profile.team) && <><br />{[d.profile.age && `${d.profile.age} años`, d.profile.position, d.profile.team].filter(Boolean).join(" · ")}</>}
            </div>
          </Box>
          <Box icon={ShieldCheck} title="Vigencia del servicio" testid="my-area-service">
            <div style={{ fontSize: 18, fontWeight: 700, color: SVC_COLOR[s.status] }} data-testid="my-area-service-status">{s.status_label}</div>
            {s.end_date && <div style={{ fontSize: 13, color: "#d4d4d4", marginTop: 4 }}>
              {s.status === "expired" ? `Finalizó el ${fmt(s.end_date)}` : `Hasta el ${fmt(s.end_date)} · ${s.days_left === 0 ? "termina hoy" : `quedan ${s.days_left} días`}`}
            </div>}
            {s.status === "expired" && <div style={{ fontSize: 12, color: "#fca5a5", marginTop: 8 }}>Puedes consultar tu historial y planificaciones anteriores. Para continuar, contacta con tu entrenador.</div>}
          </Box>
          <Box icon={ClipboardCheck} title="Planificación" testid="my-area-planning">
            {d.planning.state === "published" ? (<>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#4ade80" }}>Aprobada y publicada</div>
              <div style={{ fontSize: 13, color: "#a3a3a3", marginTop: 4 }}>Desde el {fmt(d.planning.published_at)}</div>
              {d.planning.update_unseen && <div style={{ fontSize: 13, color: "#facc15", marginTop: 6 }} data-testid="my-area-update">Hay una versión actualizada de tu planificación.</div>}
              {d.planning.update_in_review && <div style={{ fontSize: 12, color: "#a3a3a3", marginTop: 6 }}>Tu entrenador prepara una actualización.</div>}
            </>) : (
              <div style={{ fontSize: 15, display: "flex", gap: 8, alignItems: "center" }} data-testid="my-area-planning-pending">
                <Clock size={16} color="#facc15" />{d.planning.state === "pending_review" ? "Pendiente de revisión por tu entrenador" : "Aún sin planificación asignada"}
              </div>
            )}
            <Link to="/app/mi-planificacion" className="emp-linkbtn" style={{ display: "inline-block", color: "#fff" }} data-testid="my-area-go-planning">Ver mi planificación →</Link>
          </Box>
          <Box icon={CalendarDays} title="Sesiones" testid="my-area-sessions">
            {d.next_session ? (<>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{d.next_session.title || d.next_session.day_type}</div>
              <div style={{ fontSize: 13, color: "#a3a3a3", marginTop: 4 }}>Próxima: {fmt(d.next_session.date)}</div>
            </>) : <div style={{ fontSize: 14, color: "#a3a3a3" }}>No hay sesiones próximas.</div>}
            <div style={{ fontSize: 12, color: "#737373", marginTop: 6 }}>Ejercicios, vídeos, series, repeticiones y descansos están en cada sesión.</div>
            {d.role === "client" && <Link to="/app/entrenamiento" className="emp-linkbtn" style={{ display: "inline-block", color: "#fff" }} data-testid="my-area-go-calendar">Abrir calendario →</Link>}
          </Box>
          {d.onboarding && (
            <Box icon={ClipboardCheck} title="Incorporación" testid="my-area-onboarding">
              <div style={{ fontSize: 16, fontWeight: 700 }}>{ONB[d.onboarding.status] || d.onboarding.status}</div>
              {d.onboarding.submitted_at && <div style={{ fontSize: 13, color: "#a3a3a3", marginTop: 4 }}>Formulario enviado el {fmt(d.onboarding.submitted_at)}</div>}
            </Box>
          )}
        </div>
      )}
    </AppShell>
  );
}
